import { useState, useMemo, useEffect } from "react";
import "./AppCalendar.css";
import { apiGetCompanyHolidaysForWorkspace } from "../../../services/PaidTimeOffPolicyService";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import { CompanyHoliday, CompanyHolidayType } from "winslow-core";
import { convertToLocalTimezone, getNthDayOfWeek, getLastWeekdayOfMonth } from "../app-time-off/companyHolidayUtils";

export type CalendarItemType = "COMPANY_HOLIDAY" | "TIME_OFF" | "EVENT";

export type CalendarItem = {
    type: CalendarItemType;
    name: string;
    employeeRecordId?: string;
    startDate: Date;
    endDate: Date;
}

export type CurrentYearHoliday = {
    name: string;
    date: Date;
}

type AppCalendarProps = {
    items: CalendarItem[];
    onItemClick?: (item: CalendarItem) => void;
}

export const AppCalendar = ({ items, onItemClick }: AppCalendarProps) => {
    const storeUser = useSelector(selectUser);

    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentYearHolidays, setCurrentYearHolidays] = useState<CurrentYearHoliday[]>([]);
    const [companyHolidays, setCompanyHolidays] = useState<CompanyHoliday[]>([]);

    useEffect(() => {
        if (!storeUser?.currentWorkspaceId) {
            return;
        }

        (async () => {
            const theHolidays = await apiGetCompanyHolidaysForWorkspace(storeUser.currentWorkspaceId || "");

            if (theHolidays) {
                setCompanyHolidays(theHolidays);
            }
        })();
    }, [storeUser]);

    useEffect(() => {

        if (companyHolidays) {
            const holidaysProcessed = companyHolidays.map((holiday) => {
                let currentYear: Date | null = null;

                switch (holiday.type) {
                    case CompanyHolidayType.DATE:
                        if (holiday.date) {
                            const useDate = convertToLocalTimezone(holiday.date);
                            currentYear = new Date(currentDate.getFullYear(), useDate.getMonth(), useDate.getDate());

                            console.log("[AppCompanyHolidays] holiday.date: ", holiday.date);
                            console.log("[AppCompanyHolidays] useDate: ", useDate);
                            console.log("[AppCompanyHolidays] currentYear: ", currentYear);
                        }
                        break;
                    case CompanyHolidayType.NTH:
                        if (holiday.nth && holiday.dayOfWeek && holiday.month) {
                            currentYear = getNthDayOfWeek(holiday.nth, holiday.dayOfWeek, holiday.month, currentDate.getFullYear());
                        }
                        break;
                    case CompanyHolidayType.LAST:
                        if (holiday.dayOfWeek && holiday.month) {
                            currentYear = getLastWeekdayOfMonth(holiday.dayOfWeek, holiday.month, currentDate.getFullYear());
                        }
                        break;  
                    // case CompanyHolidayType.DAY_AFTER:
                    //     if (holiday.date) {
                    //         const nextDay = getNextDay(holiday.date);
                    //         thisYear = new Date(nextDay.getFullYear(), nextDay.getMonth(), nextDay.getDate());
                    //         nextYear = new Date(nextDay.getFullYear() + 1, nextDay.getMonth(), nextDay.getDate());
                    //     }
                }

                return {
                    name: holiday.name,
                    date: currentYear
                } as CurrentYearHoliday;
            })

            // holidaysProcessed.sort((a, b) => {
            //     if (a.date && b.date) {
            //         return a.date.getTime() - b.date.getTime();
            //     } else if (a.date && !b.date) {
            //         return -1;
            //     } else if (!a.date && b.date) {
            //         return 1;
            //     } else {
            //         return 0;
            //     }
            // })

            setCurrentYearHolidays(holidaysProcessed);
        
        }
    }, [currentDate, companyHolidays]);
    
    const daysInMonth = useMemo(() => {
        return new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
    }, [currentDate]);

    const firstDayOfMonth = useMemo(() => {
        return new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
    }, [currentDate]);

    const monthYearString = useMemo(() => {
        return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    }, [currentDate]);

    const weeks = useMemo(() => {
        const days: (number | null)[] = Array(firstDayOfMonth).fill(null);
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(i);
        }
        
        const weeks: (number | null)[][] = [];
        while (days.length) {
            weeks.push(days.splice(0, 7));
        }
        
        // Pad the last week if needed
        const lastWeek = weeks[weeks.length - 1];
        if (lastWeek.length < 7) {
            lastWeek.push(...Array(7 - lastWeek.length).fill(null));
        }
        
        return weeks;
    }, [daysInMonth, firstDayOfMonth]);

    const getItemsForDay = (day: number) => {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        return items.filter(item => {
            const start = new Date(item.startDate);
            const end = new Date(item.endDate);
            return date >= start && date <= end;
        });
    };

    const handlePrevMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    };

    const handleNextMonth = () => {
        setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    };

    const handleToday = () => {
        setCurrentDate(new Date());
    };

    const checkIsToday = (dayNum: number): boolean => {
        const today = new Date();
        return dayNum === today.getDate() && 
               currentDate.getMonth() === today.getMonth() && 
               currentDate.getFullYear() === today.getFullYear();
    };

    console.log("[AppCalendar] currentDate: ", currentDate);
    console.log("[AppCalendar] currentYearHolidays: ", currentYearHolidays);
    console.log("[AppCalendar] companyHolidays: ", companyHolidays);

    return (
        <div className="app-calendar-container">
            <div className="app-calendar-header">
                <h2>{monthYearString}</h2>
                <div className="app-calendar-controls">
                    <button onClick={handlePrevMonth}>&lt; Previous</button>
                    <button onClick={handleToday}>Today</button>
                    <button onClick={handleNextMonth}>Next &gt;</button>
                </div>
            </div>

            <div className="app-calendar-grid">
                <div className="app-calendar-weekdays">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                        <div key={day} className="app-calendar-weekday">{day}</div>
                    ))}
                </div>

                <div className="app-calendar-days">
                    {weeks.map((week, weekIndex) => (
                        <div key={weekIndex} className="app-calendar-week">
                            {week.map((day, dayIndex) => {
                                const dayIsToday = day ? checkIsToday(day) : false;
                                const dayItems = day ? getItemsForDay(day) : [];
                                
                                return (
                                    <div
                                        key={dayIndex}
                                        className={`app-calendar-day ${!day ? 'empty' : ''} ${dayIsToday ? 'today' : ''}`}
                                    >
                                        {day && (
                                            <>
                                                <span className="app-calendar-day-number">{day}</span>
                                                <div className="app-calendar-day-items">
                                                    {dayItems.map((item, index) => (
                                                        <div
                                                            key={index}
                                                            className={`app-calendar-item ${item.type.toLowerCase()}`}
                                                            onClick={() => onItemClick?.(item)}
                                                            title={`${item.name} (${item.type})`}
                                                        >
                                                            {item.name}
                                                        </div>
                                                    ))}
                                                    {currentYearHolidays.map((holiday, index) => {
                                                        const isHolidayToday = holiday.date.getDate() === day && 
                                                                               holiday.date.getMonth() === currentDate.getMonth() && 
                                                                               holiday.date.getFullYear() === currentDate.getFullYear();

                                                        if (!isHolidayToday) {
                                                            return null;
                                                        }

                                                        return (
                                                            <div
                                                                key={index}
                                                                className={`app-calendar-item company_holiday`}
                                                                title={holiday.name}
                                                            >
                                                                {holiday.name}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}; 